import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Header from "../../../components/Header";
import BreadcrumbsPage from "../../../components/Breadcrumbs";
import Input from "../../../components/Input";
import Button from "../../../components/Button";
import ChannelAPI from "../../../api/channel/channel";
import UserApi from "../../../api/user/user";
import { Spinner } from "../../../components/Spinner";

import IconMessage from "../../../assets/icons/message-icon.svg";
import IconCommunity from "../../../assets/icons/fone-icon.svg";
import IconFile from "../../../assets/icons/icon-file.svg";
import IconPoints from "../../../assets/icons/icon-benefits.svg";
import UserTie from "../../../assets/icons/user-tie.svg";
import User from "../../../assets/icons/icon-user.svg";

import { StatusToggle } from "../../../components/StatusToggle/StatusToggle";

const newUserSchema = z
  .object({
    name: z.string().min(1, "Nome é obrigatório"),
    email: z.string().email("E-mail inválido"),
    password: z.string().min(6, "A senha deve ter no mínimo 6 caracteres"),
    confirmPassword: z.string().min(6, "Confirme a senha corretamente"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

type NewUserSchema = z.infer<typeof newUserSchema>;

const modulesData = [
  {
    title: "Atendimento",
    id: 1,
    icon: IconMessage,
    active: true,
    subModules: [
      { title: "Mensagens", id: 101, active: true },
      { title: "Canais", id: 102, active: true },
    ],
  },
  { title: "Proprietários", id: 2, icon: UserTie, active: true },
  { title: "Comunicados", id: 3, icon: IconCommunity, active: true },
  {
    title: "Documentos",
    id: 4,
    icon: IconFile,
    active: true,
    subModules: [
      { title: "Lista de documentos", id: 401, active: true },
      { title: "Categorias", id: 402, active: true },
    ],
  },
  {
    title: "Programa de fidelidade",
    id: 5,
    icon: IconPoints,
    active: true,
    subModules: [
      { title: "Bônus", id: 501, active: true },
      { title: "Benefícios", id: 502, active: true },
    ],
  },
  { title: "Gestão de usuários", id: 6, icon: User, active: true },
];

export default function NewUser() {
  const [chatTopics, setChatTopics] = useState<any[]>([]);
  const [modules, setModules] = useState(modulesData);
  const networkUnitId = Number(localStorage.getItem("selectedNetwork") || 0);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const isEdit = location.state?.isEdit || false;
  const dataEdit = location.state?.data || null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<NewUserSchema>({
    resolver: zodResolver(newUserSchema),
  });

  useEffect(() => {
    if (isEdit && dataEdit) {
      setValue("name", dataEdit.name || "");
      setValue("email", dataEdit.email || "");
    }
  }, [isEdit, dataEdit, setValue]);

  useEffect(() => {
    const fetchChatTopics = async () => {
      setLoading(true);
      try {
        const topics = await ChannelAPI.listChatTopics(networkUnitId);
        setChatTopics(
          topics.map((topic: any) => ({
            ...topic,
            active: topic.Active,
          }))
        );
      } catch (error) {
        console.error("Erro ao carregar canais de atendimento", error);
      } finally {
        setLoading(false);
      }
    };
    fetchChatTopics();
  }, [networkUnitId]);

  const toggleModuleStatus = (index: number) => {
    const updated = [...modules];
    const currentModule = updated[index];
    currentModule.active = !currentModule.active;

    if (!currentModule.active && Array.isArray(currentModule.subModules)) {
      currentModule.subModules = currentModule.subModules.map((sub) => ({
        ...sub,
        active: false,
      }));
    }

    setModules(updated);
  };

  const toggleSubModuleStatus = (moduleIndex: number, subIndex: number) => {
    const updated = [...modules];
    const currentModule = updated[moduleIndex];
    if (!currentModule || !currentModule.subModules) return;

    const sub = currentModule.subModules[subIndex];
    sub.active = !sub.active;

    const algumSubAtivo = currentModule.subModules.some((s) => s.active);
    currentModule.active = algumSubAtivo;

    setModules(updated);
  };

  const toggleChannelStatus = (index: number) => {
    const updated = [...chatTopics];
    updated[index].active = !updated[index].active;
    setChatTopics(updated);
  };

  const onSubmit = async (data: NewUserSchema) => {
    setLoading(true);
    try {
      if (!isEdit) {

        const createPayload = {
          name: data.name,
          email: data.email,
          password: data.password,
          confirmPassword: data.confirmPassword,
          networkUnitId,
          chatTopics: chatTopics.map((t) => ({
            chaTopicId: t.Id,
            active: t.active,
          })),
          modules: modules.flatMap((m) => [
            { modulesId: m.id, active: m.active },
            ...(m.subModules?.map((s) => ({
              modulesId: s.id,
              active: s.active,
            })) || []),
          ]),
        };
        await UserApi.createUser(createPayload);
      } else {

        const updatePayload = {
          id: dataEdit.id,
          name: data.name,
          email: data.email,
          address: dataEdit.address || "",
          phone: dataEdit.phone || "",
          deviceId: dataEdit.deviceId || "",
          networkUnitId,
          newPassword: data.password,
          newPasswordConfirm: data.confirmPassword,
          chatTopics: chatTopics.map((t) => ({
            chaTopicId: t.Id,
            active: t.active,
          })),
          modules: modules.flatMap((m) => [
            { modulesId: m.id, active: m.active },
            ...(m.subModules?.map((s) => ({
              modulesId: s.id,
              active: s.active,
            })) || []),
          ]),
        };
        await UserApi.updateUser(dataEdit.id, updatePayload);
      }
      navigate("/gestao-usuarios");
    } catch (error) {
      console.error("Erro ao cadastrar/atualizar usuário:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="new-user-page"
      className="flex flex-col justify-between h-screen pl-10 pt-12"
    >
      <div className="flex flex-col gap-2">
        <BreadcrumbsPage />
        <div id="header-container" className="w-1/2">
          <Header
            arrowIcon={true}
            title={isEdit ? "Editar Usuário" : "Cadastrar Novo Usuário"}
          />
        </div>
      </div>

      <div
        id="section-dados-gerais"
        className="flex flex-col gap-6 w-full shadow-custom rounded-lg p-6"
      >
        <h3 id="header-dados-gerais" className="font-main text-lg font-bold">
          Dados Gerais
        </h3>
        <form
          id="user-form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <div className="flex gap-4">
            <div className="w-1/2">
              <Input
                id="input-name"
                label="Nome"
                type="text"
                placeholder="Digite o nome do usuário"
                register={register("name")}
                errors={errors.name}
              />
            </div>
            <div className="w-1/2">
              <Input
                id="input-email"
                label="E-mail"
                type="email"
                placeholder="Digite o e-mail do usuário"
                register={register("email")}
                errors={errors.email}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-1/2">
              <Input
                id="input-password"
                label="Senha"
                type="password"
                placeholder="Digite a senha"
                register={register("password")}
                errors={errors.password}
                password
              />
            </div>
            <div className="w-1/2">
              <Input
                id="input-confirm-password"
                label="Confirmar Senha"
                type="password"
                placeholder="Confirme a senha"
                register={register("confirmPassword")}
                errors={errors.confirmPassword}
                password
              />
            </div>
          </div>
        </form>
      </div>

      <div
        id="section-modules"
        className="flex flex-col gap-4 w-full shadow-custom rounded-lg p-6"
      >
        <h3 id="header-modules" className="font-main text-lg font-bold">
          Módulos
        </h3>

        <div className="flex flex-col">
          {modules.map((module, idx) => (
            <React.Fragment key={module.id}>
              <div className="flex items-center justify-between py-4 px-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="bg-[#27365B] p-2 rounded-lg w-10 h-10 flex items-center justify-center">
                    <img
                      src={module.icon}
                      alt={module.title}
                      className="w-5 h-5 filter brightness-0 invert"
                    />
                  </div>
                  <span className="font-semibold text-base">
                    {module.title}
                  </span>
                </div>
                <StatusToggle
                  active={module.active}
                  onToggle={() => toggleModuleStatus(idx)}
                />
              </div>

              {Array.isArray(module.subModules) &&
                module.subModules.map((sub, subIdx) => (
                  <div
                    key={sub.id}
                    className="flex items-center justify-between py-4 border-b border-gray-200 px-4"
                  >
                    <span className="text-gray-700 pl-14">{sub.title}</span>

                    <StatusToggle
                      active={sub.active}
                      onToggle={() =>
                        toggleSubModuleStatus(idx, subIdx)
                      }
                    />
                  </div>
                ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div
        id="section-channels"
        className="flex flex-col gap-4 w-full shadow-custom rounded-lg p-6 mt-6"
      >
        <h3 id="header-channels" className="font-main text-lg font-bold">
          Canais de atendimento
        </h3>
        {loading ? (
          <div id="channels-loading" className="text-center">
            Carregando...
          </div>
        ) : chatTopics.length > 0 ? (
          chatTopics.map((channel, chIdx) => (
            <div
              key={channel.Id}
              id={`channel-row-${channel.Id}`}
              className="flex justify-between py-2 border-b border-gray-200"
            >
              <span
                id={`channel-title-${channel.Id}`}
                className="font-medium"
              >
                {channel.Title}
              </span>
              <StatusToggle
                active={channel.active}
                onToggle={() => toggleChannelStatus(chIdx)}
              />
            </div>
          ))
        ) : (
          <div id="channels-empty" className="text-center text-gray-500">
            Nenhum canal disponível.
          </div>
        )}
      </div>
      
      <div
        id="actions-bar"
        className="flex justify-between mt-auto shadow-custom p-10"
      >
        <Button
          id="button-back"
          title="Voltar"
          onClick={() => navigate(-1)}
          backGroundColor="#FCFCFA"
          color="#F28E00"
          borderRadius={8}
        />
        {loading ? (
          <Spinner />
        ) : (
          <Button
            id="button-submit"
            title={isEdit ? "Atualizar Usuário" : "Cadastrar Usuário"}
            backGroundColor="#F28E00"
            color="#FAFAFC"
            borderRadius={8}
            type="submit"
            form="user-form"
          />
        )}
      </div>
    </div>
  );
}
